import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Heart, UserCheck, Archive, ExternalLink } from "lucide-react";

export default function ParProfile() {
  const { handle } = useParams();
  const { t } = useTranslation();
  const [couple, setCouple] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/par/${handle}`)
      .then((r) => setCouple(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Par-profil ikke funnet"))
      .finally(() => setLoading(false));
  }, [handle]);

  if (loading) return <Layout><div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div></Layout>;
  if (err) return <Layout><div className="text-[var(--magenta)] font-meta">{err}</div></Layout>;
  if (!couple) return null;

  const isArchived = couple.status === "archived";
  const isSolo     = couple.status === "solo";
  const showPartners = couple.visibility !== "private";

  return (
    <Layout>
      <div className="max-w-4xl">
        {isArchived && (
          <div className="mb-6 px-4 py-3 border-l-2 border-amber-500 bg-amber-500/5 text-xs font-meta text-amber-300" data-testid="par-archived-banner">
            <Archive className="h-3.5 w-3.5 inline mr-2" />
            DENNE PAR-PROFILEN ER ARKIVERT – LESBAR, MEN INGEN NYE INNLEGG.
          </div>
        )}
        {isSolo && (
          <div className="mb-6 px-4 py-3 border-l-2 border-emerald-500 bg-emerald-500/5 text-xs font-meta text-emerald-300">
            <UserCheck className="h-3.5 w-3.5 inline mr-2" />
            SOLO-PROFIL – DRIVES AV ÉN PARTNER ETTER PARETS OPPLØSNING.
          </div>
        )}

        <div className="flex items-center gap-4 mb-6">
          <Heart className="h-8 w-8 text-[var(--magenta)] shrink-0" />
          <div>
            <div className="font-meta mb-1">PAR-PROFIL</div>
            <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-tight" data-testid="par-name">
              {couple.name}
            </h1>
            <div className="font-manrope text-sm text-[var(--cream-soft)] mt-1">@{couple.handle}</div>
          </div>
        </div>

        {couple.bio && (
          <div className="font-manrope text-[var(--cream)] text-base mb-8 max-w-2xl leading-relaxed" data-testid="par-bio">
            {couple.bio}
          </div>
        )}

        {showPartners && (
          <div className="panel-tight p-6 mb-8">
            <div className="font-meta mb-4">PARTNERE</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[couple.partner_a, couple.partner_b].filter(Boolean).map((p) => (
                <Link
                  key={p.id}
                  to={`/profile/${p.username}`}
                  className="flex items-center gap-3 group hover:text-[var(--magenta)]"
                  data-testid={`par-partner-${p.username}`}
                >
                  <div className="h-12 w-12 rounded-sm border border-white/20 bg-[var(--magenta)]/40 flex items-center justify-center font-meta text-[10px] text-black">
                    {p.username?.slice(0,2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-fraunces italic text-lg text-[var(--cream)] group-hover:text-[var(--magenta)] truncate">
                      {p.username}
                    </div>
                    <div className="font-manrope text-xs text-[var(--cream-soft)] flex items-center gap-1">
                      Profil <ExternalLink className="h-2.5 w-2.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 panel-tight p-5 text-xs text-[var(--cream-soft)] leading-relaxed">
          <strong className="text-[var(--cream)]">Om par-profiler:</strong> En par-profil drives sammen
          av to single-medlemmer. Hvert innlegg er signert med hvem av de to som faktisk skrev. Hvis én
          partner forlater, arkiveres profilen. Gjenværende partner kan deretter søke om å beholde
          profilen som «solo-profil» med samtykke fra den forlatende.
        </div>
      </div>
    </Layout>
  );
}
